import { createAsyncThunk } from "@reduxjs/toolkit";
import { getError } from "../../../services/lib/getError";
import {
  generateCaseSolutionPrompt,
  getAiCaseExamples,
  getAiTip,
  postAiScoringCase,
} from "../../../services/api/instances/casesApi/methods";
import { aggregateDataForAiScoring } from "../helpers/aggregateDataForAiScoring";
import {
  CasesValue,
  TAiScoringRequest,
  TAiScoringValidationCase,
  TCaseExamples,
  TCaseObjectKeys,
  TOldCaseAgentHarness,
} from "../../../services/api/instances/casesApi/types";
import { RootState } from "../../../services/redux/types";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { clearAiExamples, setCaseValue } from "./slice";
import { aggregateDataForAiTip } from "../helpers/aggregateDataForAiTip";
import { transferCaseExampleToCaseValue } from "../helpers/transfetCaseExampleToCaseValue";
import { getUpdatedCaseValue } from "../helpers/getUpdatedCaseValue";
import { convertCaseAgentHarness } from "../helpers";

export const getAiScoringAction = createAsyncThunk(
  "cases/getAiScoring",
  async (
    value: CasesValue | null,
    { dispatch, getState, rejectWithValue, signal },
  ) => {
    const { selectedId, data } = (getState() as RootState).storageObjectState
      .cases;
    const { caseValue } = (getState() as RootState).casesState;
    const finalValue = value ? value : caseValue;

    if (!selectedId || !finalValue) return;
    dispatch(
      editStorageObject({
        id: data.entities[selectedId]?.id as string,
        name: finalValue?.case_object?.case_info?.name ?? "",
        value: finalValue,
        service: "cases",
      }),
    );
    try {
      const postObj = aggregateDataForAiScoring(finalValue, null);

      const result = await postAiScoringCase(postObj, signal);
      const {
        task_description,
        input_artifact,
        output_artifact,
        consumer,
        skills,
        output_attributes,
        knowledge,
        ...rest
      } = result.validation;
      const aiScoring: TAiScoringValidationCase = {
        task_description,
        input_artifact,
        output_artifact,
        consumer,
        skills,
        output_attributes,
        knowledge,
        summary: { ...rest },
      };

      if (!selectedId || !finalValue) return;
      const updatedValue: CasesValue = {
        ...finalValue,
        ai_scoring: {
          ...aiScoring,
        },
      };

      if (!value) {
        dispatch(setCaseValue(updatedValue));
      }
      dispatch(
        editStorageObject({
          id: data.entities[selectedId]?.id as string,
          name: updatedValue?.case_object?.case_info?.name ?? "",
          value: updatedValue,
          service: "cases",
        }),
      );

      return aiScoring;
    } catch (e: unknown) {
      if (e instanceof Error && e.name === "AbortError") return;
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAiExamplesAction = createAsyncThunk(
  "cases/getAiExamples",
  async (query: string | null, { dispatch, getState, rejectWithValue }) => {
    try {
      const { selectedId } = (getState() as RootState).storageObjectState.cases;
      const { caseValue } = (getState() as RootState).casesState;
      const postObj = aggregateDataForAiScoring(caseValue, query);

      dispatch(clearAiExamples(selectedId ?? ""));

      const result = await getAiCaseExamples(postObj);
      if (!selectedId || !result) return;
      return { selectedId, result };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAiTipAction = createAsyncThunk(
  "cases/getAiTip",
  async (field: TCaseObjectKeys, { dispatch, getState, rejectWithValue }) => {
    try {
      const { caseValue } = (getState() as RootState).casesState;
      const postObj = aggregateDataForAiTip(caseValue, field);
      const result = await getAiTip(postObj);
      return result;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const transferCaseExampleAction = createAsyncThunk(
  "cases/transferCaseExample",
  async (
    {
      example,
      keys,
    }: { example: TCaseExamples; keys?: Array<keyof TCaseExamples> },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const { caseValue } = (getState() as RootState).casesState;
      if (!caseValue) return;
      const newValue = transferCaseExampleToCaseValue(example, caseValue, keys);
      dispatch(setCaseValue(newValue));
      dispatch(getAiScoringAction(newValue));
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const generateCaseSolutionPromptAction = createAsyncThunk(
  "cases/generateCaseSolutionPrompt",
  async (
    postObject: TAiScoringRequest,
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .cases;
      const { caseValue } = (getState() as RootState).casesState;
      if (!caseValue || !selectedId) return;
      const result = await generateCaseSolutionPrompt(postObject);
      const updatedValue: CasesValue = {
        ...caseValue,
        solution_prompt: {
          solution_type: result.solution_type,
          next_steps: result.next_steps,
          initial_prompt: result.initial_prompt,
        },
      };
      dispatch(setCaseValue(updatedValue));
      dispatch(
        editStorageObject({
          id: selectedId,
          name: data.entities[selectedId].friendly_name ?? "",
          value: updatedValue,
          service: "cases",
        }),
      );
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const addNewOutputAttributesFieldAction = createAsyncThunk(
  "cases/addNewOutputAttributesField",
  async (value: CasesValue, { getState, dispatch, rejectWithValue }) => {
    try {
      const { selectedId, data } = (getState() as RootState).storageObjectState[
        "cases"
      ];
      if (!value || !selectedId) return;
      const selectedObject = data.entities[selectedId];
      const updatedValue = getUpdatedCaseValue(value, {
        case_transform_method: {
          ...value.case_object?.case_transform_method,
          output_attributes: "",
        },
      });

      dispatch(setCaseValue(updatedValue));

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "cases",
        }),
      );
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateCaseWithNewAgentSchemaAction = createAsyncThunk(
  "cases/updateCaseWithNewAgentSchema",
  async (
    { value, id }: { value: CasesValue; id: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      if (!value || value?.metadata?.has_new_agent_schema) return;
      const newAgentSchema = convertCaseAgentHarness(
        value.agent_harness as TOldCaseAgentHarness,
      );
      console.log("MEW");
      const newValue: CasesValue = {
        ...value,
        agent_harness: newAgentSchema,
        metadata: {
          ...value.metadata,
          has_new_agent_schema: true,
        },
      };
      dispatch(setCaseValue(newValue));
      dispatch(
        editStorageObject({
          id,
          name: value?.case_object?.case_info?.name,
          value: newValue,
          service: "cases",
        }),
      );
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
